import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Rail, Section } from "@hybrid-iptv/ui";
import { runsOnDevice } from "../api/client.js";
import { useProfiles } from "../settings/profileStore.js";
import { ConnectYoutube } from "../social/ConnectYoutube.js";
import { CREATOR_CATEGORIES } from "../social/creators.js";
import { buildSocialRows, starterRows, type SocialRow } from "../social/socialRows.js";
import { creatorsFor, useSocialStore } from "../social/socialStore.js";
import { useSubscriptions } from "../social/subscriptions.js";
import { useCreators, youtubeWatchUrl, type CreatorChannel } from "../social/youtubeApi.js";
import { useYoutubeConnection } from "../social/youtubeAccount.js";
import { TrailerModal } from "../library/TrailerModal.js";
import { youtubeEmbedUrl } from "../library/vodHooks.js";
import { reachesTop, showBanner } from "../home/homeFocus.js";

/**
 * Social (roadmap Section 24; build guide docs/social-connect-youtube.md).
 *
 * Connecting YouTube is OPTIONAL (owner, 2026-10-13: "make the sign in optional for social, if
 * they don't just fall back to our nested rows"):
 *  - a profile that has connected its own YouTube sees its own subscriptions: "Latest from your
 *    subscriptions", then topic rows;
 *  - a profile that has not, on the website, or in a build without the sign-in, sees the starter
 *    creators in category rows, as before, with a Connect YouTube prompt above them where
 *    connecting is possible;
 *  - a Kids profile sees only a message.
 *
 * In every row whatever is live comes first, marked LIVE in words as well as the dot, with no
 * separate live row (owner). The order is set when the page opens or the mode changes; the
 * 3-minute live refresh only updates a tile in place and never moves it.
 *
 * OK plays the video in the trailer window (YouTube's embedded player in the app). A video its
 * owner will not let play embedded says so and stays here: nothing leaves the app (owner,
 * 2026-10-12), so there is no hand-off to the YouTube app.
 */
export function SocialPage() {
  const profile = useProfiles((state) => state.profiles.find((p) => p.id === state.activeProfileId));
  const lists = useSocialStore((state) => state.lists);
  // null = still learning, so the page never flashes the wrong state.
  const { available: canConnect, connected } = useYoutubeConnection(profile?.id);
  const mode: "kids" | "waiting" | "subscriptions" | "starter" = profile?.isKids
    ? "kids"
    : canConnect === null || (canConnect && connected === null)
      ? "waiting"
      : canConnect && connected
        ? "subscriptions"
        : "starter";

  const starter = useMemo(() => creatorsFor(profile, lists), [profile, lists]);
  const subscriptions = useSubscriptions(profile?.id, mode === "subscriptions");
  const feedIds = useMemo(
    () =>
      mode === "subscriptions"
        ? (subscriptions.channels ?? []).map((channel) => channel.id)
        : mode === "starter"
          ? starter.map((creator) => creator.channelId)
          : [],
    [mode, subscriptions.channels, starter],
  );
  const { data, isLoading, isError } = useCreators(feedIds);
  const [playing, setPlaying] = useState<{ videoId: string; name: string } | null>(null);
  // The video whose owner only lets it play on YouTube, said to the viewer in a dialog.
  const [cantPlay, setCantPlay] = useState<string | null>(null);
  // The tile that was pressed, so Back and Close land on it again.
  const pressed = useRef<HTMLElement | null>(null);
  const giveFocusBack = () => {
    const tile = pressed.current;
    requestAnimationFrame(() => {
      if (tile?.isConnected) tile.focus();
    });
  };

  const answers = useMemo(() => new Map((data?.channels ?? []).map((c) => [c.id, c])), [data]);
  const names = useMemo(
    () =>
      new Map<string, string>(
        mode === "subscriptions"
          ? (subscriptions.channels ?? []).map((c) => [c.id, c.title] as const)
          : starter.map((c) => [c.channelId, c.name] as const),
      ),
    [mode, subscriptions.channels, starter],
  );

  // The order is computed once for this profile and mode, when the first answer arrives, and not
  // again while the page is open: later live refreshes change a tile's badge, never its place.
  const builtKey = profile ? `${profile.id}:${mode}` : null;
  const [built, setBuilt] = useState<{ key: string; rows: SocialRow[] } | null>(null);
  useEffect(() => {
    if (!builtKey || !data || (mode !== "subscriptions" && mode !== "starter")) return;
    if (mode === "subscriptions" && !subscriptions.channels) return;
    setBuilt((current) =>
      current && current.key === builtKey
        ? current
        : {
            key: builtKey,
            rows:
              mode === "subscriptions"
                ? buildSocialRows(subscriptions.channels!, answers, data.liveChecked)
                : starterRows(starter, CREATOR_CATEGORIES, answers, data.liveChecked),
          },
    );
  }, [builtKey, mode, data, answers, subscriptions.channels, starter]);
  const rows = built && built.key === builtKey ? built.rows : null;

  const liveOf = (id: string) => (data?.liveChecked ? answers.get(id)?.live ?? null : null);
  const liveCount = rows ? new Set(rows.flatMap((row) => row.channelIds).filter((id) => liveOf(id))).size : 0;

  // Back to the top, heading and all, when focus reaches the rail's collapse
  // arrow (or anything else at or above Home, the same cut as Home's) or the
  // first row, whichever row that is right now (owner, 2026-10-09). Next frame,
  // after the D-pad's own scrollIntoView, which would otherwise stop it.
  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!target) return;
      const firstRow = document.querySelector(".page--social .social-rail");
      if (reachesTop(target) || firstRow?.contains(target)) requestAnimationFrame(showBanner);
    };
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, []);

  const heading = <h1 className="social-title">Social</h1>;

  if (mode === "kids") {
    return (
      <div className="page page--social">
        {heading}
        <p className="social-note">Social isn&apos;t available on a Kids profile.</p>
      </div>
    );
  }
  if (mode === "waiting") return <div className="page page--social">{heading}</div>;

  if (mode === "subscriptions" && subscriptions.channels && subscriptions.channels.length === 0) {
    return (
      <div className="page page--social">
        {heading}
        <p className="social-note">No subscriptions yet. Subscribe to channels on YouTube and they show up here.</p>
      </div>
    );
  }
  if (mode === "starter" && starter.length === 0) {
    return (
      <div className="page page--social">
        {heading}
        <p className="social-note">{profile?.name ?? "This profile"} doesn&apos;t follow any creators.</p>
        {canConnect && profile && <ConnectYoutube profileId={profile.id} focusOnArrival={false} />}
      </div>
    );
  }

  const failed = (mode === "subscriptions" && subscriptions.status === "error" && !subscriptions.channels) || (isError && !data);
  const loading = !failed && !rows && (isLoading || !data || (mode === "subscriptions" && subscriptions.status === "loading"));

  return (
    <div className="page page--social">
      <header className="social-head">
        {heading}
        <p className="social-note">
          {mode === "subscriptions"
            ? "Your YouTube subscriptions. A tile marked LIVE is streaming now and comes first; the others open their latest video."
            : "Creators and podcasts from YouTube. A tile marked LIVE is streaming now; the others open their latest video."}
          {data?.liveChecked && liveCount > 0 ? ` ${liveCount} live right now.` : ""}
        </p>
      </header>
      {mode === "starter" && canConnect && profile && (
        // Optional: the starter rows below are the page without it.
        <ConnectYoutube profileId={profile.id} focusOnArrival={false} />
      )}
      {mode === "starter" && canConnect === false && !runsOnDevice() && (
        <p className="social-note">Connect YouTube in the SliqueTV app on your TV to see your own subscriptions here.</p>
      )}
      {loading && <p className="social-note">Loading {mode === "subscriptions" ? "your subscriptions" : "creators"}…</p>}
      {failed && <p className="social-note">Couldn&apos;t reach YouTube just now. It tries again in a few minutes.</p>}
      {rows && rows.length === 0 && <p className="social-note">Nothing new from {mode === "subscriptions" ? "your subscriptions" : "these creators"} yet.</p>}
      {rows?.map((row) => (
        <Section key={row.id} title={row.title}>
          <Rail className="social-rail">
            {row.channelIds.map((id) => {
              const channel = answers.get(id);
              if (!channel) return null;
              const name = names.get(id) ?? channel.title;
              return (
                <SubscriptionTile
                  key={`${row.id}:${id}`}
                  name={name}
                  channel={channel}
                  live={liveOf(id) !== null}
                  onPlay={(videoId) => {
                    pressed.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
                    setPlaying({ videoId, name });
                  }}
                />
              );
            })}
          </Rail>
        </Section>
      ))}
      {playing && (
        <TrailerModal
          embedUrl={youtubeEmbedUrl(youtubeWatchUrl(playing.videoId), { autoplay: true })!}
          label={`${playing.name} video`}
          onClose={() => setPlaying(null)}
          onUnavailable={() => {
            setPlaying(null);
            setCantPlay(playing.name);
          }}
        />
      )}
      <Dialog
        open={cantPlay !== null}
        title="Can't play here"
        onClose={() => {
          setCantPlay(null);
          giveFocusBack();
        }}
      >
        This video's owner only lets it play on YouTube, so it can't play in the app.
      </Dialog>
    </div>
  );
}

function SubscriptionTile({
  name,
  channel,
  live,
  onPlay,
}: {
  name: string;
  channel: CreatorChannel;
  live: boolean;
  onPlay: (videoId: string) => void;
}) {
  // Live shows the live video; otherwise the newest upload. A tile whose live status flips while
  // the page is open changes its badge and video here, in place.
  const video = (live ? channel.live : null) ?? channel.latest;
  if (!video) return null;
  return (
    <button
      type="button"
      className="home-tile social-tile"
      onClick={() => onPlay(video.videoId)}
      aria-label={`${name}: ${live ? "live now" : "latest video"}, ${video.title}`}
    >
      <span className="social-tile__thumb">
        <img className="home-tile__art" src={video.thumbnail} alt="" loading="lazy" />
        {live && (
          <span className="social-tile__live">
            <span className="social-tile__dot" aria-hidden="true" />
            LIVE
          </span>
        )}
        <span className="social-tile__source">YouTube</span>
      </span>
      <span className="home-tile__channel">{name}</span>
      <span className={live ? "social-tile__status social-tile__status--live" : "home-tile__program"}>
        {live ? "Live now" : video.title}
      </span>
    </button>
  );
}
